import { Injectable } from '@angular/core';
import { ApplicationLanguage, DefaultLanguageForDomain, LanguagesForDomain } from './applicationLanguage';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from '../services/cookie.service';
import { MinUser, UserService } from '../../../client-user';
import { BehaviorSubject, Observable } from 'rxjs';

declare var window: any;

interface EventListener {
  callback: Function,
  once: boolean
}

@Injectable({
    providedIn: 'root'
})
export class LocationService {
    private LANGUAGE_COOKIE: string = 'gstv-locale';

    private currentUserLocaleSubject: BehaviorSubject<string>;

    private origin: string

    private eventFunctions: { [key: string]: EventListener[] } = {};

    constructor(private translateService: TranslateService,
                private cookieService: CookieService,
                private userService: UserService) {
        this.origin = new URL(window.location.href).origin;
        this.currentUserLocaleSubject = new BehaviorSubject<string>(this.userLocale());
    }

    /**
     * Returns the current domain as 2 uppercase chars.
     */
    private domain(): string {
        let result = 'DE'
        if ((this.origin != null && (this.origin.indexOf('.ch') >= 0 || this.origin.indexOf('.tv') >= 0)) || (this.origin.indexOf('easy-ch') >= 0) || (window.location.host === 'localhost:3001')) {
            result = 'CH'
        }
        return result;
    }

    /**
     * Gets the current tld or points to uat if local
     */
    public getTld() {
        if(this.origin.indexOf('.de') > 0){
            return "de"
        }
        if(this.origin.indexOf('.biz') > 0 || window.location.host === 'localhost:3000'){
            return "biz"
        }
        if(this.origin.indexOf('.ch') > 0){
            return "ch"
        }
        if(this.origin.indexOf('.tv') > 0 || window.location.host === 'localhost:3001'){
            return "tv"
        }
        if(this.origin.indexOf('.info') > 0){
          return "info"
        }
    }

    public getDomain(): string {
        return this.domain();
    }

    public isDomainDE(): boolean {
        return this.domain() === 'DE';
    }

    public isDomainCH(): boolean {
        return this.domain() === 'CH';
    }

    public defaultLocale(): string {
        return DefaultLanguageForDomain.get(this.domain());
    }

    /**
     * Returns the user's locale in a format compatible with TranslateService.
     */
    public userLocale(): string {
        const defaultLocale = DefaultLanguageForDomain.get(this.domain());
        const cookieLocale = this.cookieService.getCookie(this.LANGUAGE_COOKIE);
        const browserLocale = this.getBrowserLocaleInApplicationFormat();

        if (this.isSupportedLocale(cookieLocale)) {
            return cookieLocale;
        } else if (this.isSupportedLocale(browserLocale)) {
            return browserLocale;
        } else {
            return defaultLocale;
        }
    }

    public currentUserLocale(): Observable<string> {
        return this.currentUserLocaleSubject;
    }

    public getAvailableApplicationLanguages(): Array<ApplicationLanguage> {
        return LanguagesForDomain.get(this.domain());
    }

    public getCurrentLanguage(): ApplicationLanguage {
        return LanguagesForDomain.get(this.domain()).find(language => language.isoCode === this.userLocale());
    }

    public switchLanguage(locale: string) {
      this.cookieService.setCookie(this.LANGUAGE_COOKIE, locale, 100);
      this.translateService.use(locale);
      this.triggerEvent('language-switch')
  }

    /**
     * Formats the detected browser language as locale string in the following format:
     *    2 chars for language (lowercase), underscore, 2 chars for locale (uppercase)
     * Depending on the browser, the detected browser language can be 2 or 5 chars long!
     * E.g. de => de_DE, en_US => en_CH
     * @param locale
     * @private
     */
    private getBrowserLocaleInApplicationFormat(): string {
        const browserLanguage = navigator.language;
        return browserLanguage.substr(0, 2) + '_' + this.domain();
    }

    private isSupportedLocale(browserLocale: string) {
        return LanguagesForDomain.get(this.domain()).some(locale => {
            return locale.isoCode === browserLocale;
        });
    }

    public onLanguageSwitch(callback: (language: string) => void) {
      this.addEventListener('language-switch', () => {
          callback(this.userLocale())
      })
    }

    private addEventListener(event: string, callback: Function, once?: boolean) {
      if (this.eventFunctions[event]) {
          this.eventFunctions[event].push({
              callback: callback,
              once: once
          });
      } else {
          this.eventFunctions[event] = [{
              callback: callback,
              once: once
          }];
      }
    }

    private triggerEvent(event: string) {
      let callbacks = this.eventFunctions[event];
      if (callbacks) {
          let del: number[] = [];
          callbacks.forEach((f, i) => {
              f.callback();
              if (f.once) {
                  del.unshift(i);
              }
          });
          del.forEach(i => {
              this.eventFunctions[event].splice(i, 1);
          })
      }
  }
}
